import type { RGNEvent } from '@/lib/api/schemas'

import type { StadiumPhase } from './derivePhase'

/**
 * Tournament Stadium — top chrome strip.
 *
 * Persists across phase transitions (sits OUTSIDE the AnimatePresence
 * crossfade in Stadium.tsx) so the title, prize pool, and entrant
 * count never "blink" mid-event. Only the phase pill in the top-right
 * mutates as the event progresses.
 *
 * Sized for 1080p first; scales via the .stadium-root base font-size
 * + clamp() on the headline so 4K display preserves the broadcast feel.
 *
 * Layout:
 *
 *   [TITLE                                 [LIVE pulse]  PHASE LABEL]
 *   [format · best-of · prize · X / Y in]
 *
 * The "format / best-of / prize / entrants" line is a quiet sub-strip
 * — important context for spectators wandering past the screen, but
 * never the focus.
 */
export function StadiumHeader({
  event,
  phase,
}: {
  event: RGNEvent | null
  phase: StadiumPhase
}) {
  if (!event) return null
  const title = event.title?.trim() || 'Tournament'
  const isLive = phase === 'LIVE' || phase === 'FINAL_MATCH'
  const phaseLabel = PHASE_LABELS[phase]
  const phaseAccent = phaseAccentVar(phase)

  return (
    <header className="flex h-[clamp(72px,8vh,112px)] w-full items-center justify-between border-b border-[var(--tv-border)] bg-[var(--tv-bg-elev)] px-[clamp(20px,2.5vw,48px)]">
      <div className="flex min-w-0 flex-col gap-1">
        <h1
          className="font-display truncate font-black tracking-tight"
          style={{ fontSize: 'clamp(1.5rem,2.6vw,2.5rem)', lineHeight: 1.05 }}
        >
          {title}
        </h1>
        <SubFacts event={event} />
      </div>

      <div className="flex shrink-0 items-center gap-[clamp(10px,1.5vw,24px)]">
        {isLive && (
          <span className="inline-flex items-center gap-2.5 rounded-full border border-[var(--tv-live)]/40 bg-[var(--tv-live)]/[0.12] px-4 py-1.5">
            <span
              className="stadium-live-dot inline-block size-2.5 rounded-full"
              style={{ backgroundColor: 'var(--tv-live)' }}
              aria-hidden
            />
            <span
              className="font-display font-extrabold uppercase"
              style={{ color: 'var(--tv-live)', fontSize: '0.9em', letterSpacing: '0.18em' }}
            >
              Live
            </span>
          </span>
        )}
        <span
          className="font-display font-extrabold uppercase"
          style={{
            color: phaseAccent,
            letterSpacing: '0.22em',
            fontSize: 'clamp(0.85rem, 1vw, 1.05rem)',
          }}
        >
          {phaseLabel}
        </span>
      </div>
    </header>
  )
}

function SubFacts({ event }: { event: RGNEvent }) {
  const parts: string[] = []
  if (event.tournamentFormat) parts.push(prettyFormat(event.tournamentFormat))
  if (event.bestOf && event.bestOf > 1) parts.push(`Best of ${event.bestOf}`)
  const pool = event.prizePoolPence ?? 0
  if (pool > 0) parts.push(`£${(pool / 100).toFixed(0)} prize pool`)
  const attendeeCount = event.attendees?.length ?? event.attendeeCount ?? 0
  const max = event.maxAttendees ?? null
  if (attendeeCount > 0) {
    parts.push(max ? `${attendeeCount} / ${max} entrants` : `${attendeeCount} entrants`)
  }
  if (parts.length === 0) return null
  return (
    <p
      className="truncate font-display font-medium uppercase"
      style={{
        color: 'var(--tv-fg-muted)',
        letterSpacing: '0.18em',
        fontSize: 'clamp(0.65rem, 0.8vw, 0.85rem)',
      }}
    >
      {parts.join('  ·  ')}
    </p>
  )
}

function prettyFormat(fmt: string): string {
  return fmt
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const PHASE_LABELS: Record<StadiumPhase, string> = {
  TEASER:            'Starts Soon',
  CHECK_IN:          'Check-in',
  AWAITING_LOCK_IN:  'Drawing Bracket',
  LIVE:              'In Progress',
  FINAL_MATCH:       'Grand Final',
  COMPLETE:          'Complete',
  CANCELLED:         'Cancelled',
}

function phaseAccentVar(phase: StadiumPhase): string {
  switch (phase) {
    case 'FINAL_MATCH':
    case 'COMPLETE':
      return 'var(--tv-gold)'
    case 'LIVE':
      return 'var(--tv-live)'
    case 'CANCELLED':
      return 'var(--tv-fg-subtle)'
    default:
      return 'var(--tv-primary)'
  }
}
